import React, { useState, useEffect, useContext } from 'react';
import { createAttendance, fetchTodayAttendance } from '../../api/Attendance';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import SelfieCapture from './SelfieCapture';
import '../../css/modal.css';
import * as FiIcons from 'react-icons/fi';
import * as MdIcons from 'react-icons/md';

const MdCheckCircle = MdIcons.MdCheckCircle as unknown as React.FC;

const FiMapPin = FiIcons.FiMapPin as unknown as React.FC;
const FiCamera = FiIcons.FiCamera as unknown as React.FC;

interface QuickCheckInModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const QuickCheckInModal: React.FC<QuickCheckInModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [showSelfieCapture, setShowSelfieCapture] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [alreadyMarked, setAlreadyMarked] = useState(false);
  const [existingAttendance, setExistingAttendance] = useState<any>(null);

  // Get current user ID from JWT token
  // The JWT token contains 'id' field (as string or number)
  const currentUserId = user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0;
  const userEmail = user?.email || '';

  // Get current date and time
  const now = new Date();
  const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
  const currentTime = now.toTimeString().slice(0, 5); // HH:MM format

  useEffect(() => {
    checkExistingAttendance();
    getCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkExistingAttendance = async () => {
    if (!currentUserId) return;
    
    try {
      setCheckingExisting(true);
      const todayAttendances = await fetchTodayAttendance(currentUserId);
      const existing =
        Array.isArray(todayAttendances) && todayAttendances.length > 0
          ? todayAttendances[0]
          : null;
      
      if (existing) {
        setAlreadyMarked(true);
        setExistingAttendance(existing);
      }
    } catch (error) {
      console.error('Failed to check existing attendance:', error);
    } finally {
      setCheckingExisting(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationLoading(false);
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        setLocationError(errorMessage);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleSelfieCapture = (file: File) => {
    setSelfieFile(file);
    const previewUrl = URL.createObjectURL(file);
    setSelfiePreview(previewUrl);
    setShowSelfieCapture(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUserId) {
      toast.error('User not found. Please contact administrator.');
      return;
    }

    if (!selfieFile) {
      toast.error('Please capture a selfie');
      return;
    }

    if (!location) {
      toast.error('Location is required. Please allow location access.');
      return;
    }

    // Double check if attendance already exists
    if (alreadyMarked) {
      toast.error('You have already marked attendance for today');
      return;
    }

    try {
      setLoading(true);
      await createAttendance({
        userId: currentUserId,
        date: currentDate,
        checkInTime: currentTime,
        status: 'present',
        latitude: location.lat,
        longitude: location.lng,
        selfie: selfieFile,
      });
      toast.success('Attendance marked successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      // Check if it's a conflict error (already exists)
      if (error.message && (error.message.includes('409') || error.message.includes('already exists') || error.message.includes('Conflict'))) {
        toast.error('You have already marked attendance for today. You can only mark attendance once per day.');
        setAlreadyMarked(true);
        checkExistingAttendance();
      } else {
        toast.error(error.message || 'Failed to mark attendance');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '500px', maxWidth: '90vw' }}>
          <button className="close-btn" onClick={onClose}>&times;</button>
          <h2>Mark Attendance</h2>
          
          {checkingExisting ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div>Checking existing attendance...</div>
            </div>
          ) : alreadyMarked ? (
            <div style={{ padding: '20px' }}>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px', 
                border: '2px solid #2196f3',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>
                  <span style={{ color: '#4caf50' }}><MdCheckCircle /></span>
                </div>
                <h3 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Attendance Already Marked</h3>
                <p style={{ margin: '0 0 15px 0', color: '#666' }}>
                  You have already marked your attendance for today.
                </p>
                {existingAttendance && (
                  <div style={{ 
                    backgroundColor: '#fff', 
                    padding: '15px', 
                    borderRadius: '6px',
                    marginTop: '15px',
                    textAlign: 'left'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Date:</strong> {new Date(existingAttendance.date).toLocaleDateString()}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Check-in Time:</strong> {existingAttendance.checkInTime || 'N/A'}
                    </div>
                    <div>
                      <strong>Status:</strong> {existingAttendance.status.charAt(0).toUpperCase() + existingAttendance.status.slice(1)}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={onClose} className="primary">
                  Close
                </button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>User:</strong> {userEmail || user?.email || 'Loading...'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Date:</strong> {new Date(currentDate).toLocaleDateString()}
              </div>
              <div>
                <strong>Time:</strong> {currentTime}
              </div>
            </div>

            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', margin: 0 }}>
                  <FiMapPin /> Location
                </label>
                {locationLoading && <span style={{ fontSize: '12px', color: '#666' }}>Getting location...</span>}
                {!location && !locationLoading && (
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      background: '#2196f3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Get Location
                  </button>
                )}
              </div>
              {location ? (
                <div style={{ fontSize: '14px', color: '#4caf50' }}>
                  ✓ Location captured: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </div>
              ) : locationError ? (
                <div style={{ fontSize: '12px', color: '#f44336' }}>
                  {locationError}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Click "Get Location" to capture your current location
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                Selfie *
              </label>
              {selfiePreview ? (
                <div style={{ marginBottom: '10px' }}>
                  <img
                    src={selfiePreview}
                    alt="Selfie preview"
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '2px solid #ddd',
                      marginBottom: '10px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSelfieCapture(true)}
                    style={{
                      padding: '8px 16px',
                      background: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiCamera /> Retake Selfie
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSelfieCapture(true)}
                  style={{
                    width: '100%',
                    padding: '15px',
                    background: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}><FiCamera /></span> Capture Selfie
                </button>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" onClick={onClose} className="secondary" disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="primary" disabled={loading || !selfieFile || !location}>
                {loading ? 'Marking...' : 'Mark Attendance'}
              </button>
            </div>
          </form>
          )}
        </div>
      </div>

      {showSelfieCapture && (
        <SelfieCapture
          onCapture={handleSelfieCapture}
          onCancel={() => setShowSelfieCapture(false)}
          existingImage={selfiePreview || undefined}
        />
      )}
    </>
  );
};

export default QuickCheckInModal;
